import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { registerCompany, verifyOTP, resendOTP } from "../Backend/authFunction";
import { setAuthorization } from "../Functions/AuthFunction";
import { useGlobalContext } from "../ContextApi/GlobalContext";

const step1Schema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .min(8, "Minimum 8 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/,
      "Must include upper, lower, number & special char"
    )
    .required("Password is required"),
});

const step2Schema = yup.object().shape({
  companyName: yup.string().required("Company name is required"),
  website: yup
    .string()
    .url("Invalid URL (include https://)")
    .required("Website is required"),
  businessType: yup.string().required("Select a business type"),
  companySize: yup.string().required("Select company size"),
  country: yup.string().required("Country is required"),
});

const step3Schema = yup.object().shape({
  fullName: yup.string().required("Full name is required"),
  adminRole: yup.string().required("Job title is required"),
  phone: yup
    .string()
    .matches(/^[0-9]+$/, "Must be only digits")
    .min(10, "Must be at least 10 digits")
    .required("Phone is required"),
});

const step4Schema = yup.object().shape({
  otp: yup
    .string()
    .length(6, "OTP must be exactly 6 digits")
    .required("OTP is required"),
});

const slideUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } },
};

function RegisterPage() {
  const navigate = useNavigate();
  const { dispatch } = useGlobalContext();
  const token = localStorage.getItem("authToken");
  useEffect(() => {
    if (token) {
      navigate("/dashboard");
    }
  }, [token, navigate]);

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailData, setEmailData] = useState({ email: "" }); // Store email for OTP
  const [showPassword, setShowPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(120); // 2 minute timer (120s)

  useEffect(() => {
    let interval;
    if (step === 4 && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer, step]);

  const currentSchema =
    step === 1
      ? step1Schema
      : step === 2
      ? step2Schema
      : step === 3
      ? step3Schema
      : step4Schema;

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors },
    watch,
  } = useForm({
    resolver: step < 5 ? yupResolver(currentSchema) : undefined,
    mode: "onChange",
  });

  const passwordValue = watch("password", "");

  const handleNext = async (data) => {
    const isValid = await trigger();
    if (isValid) {
      const updatedData = { ...formData, ...data };
      setFormData(updatedData);

      if (step === 3) {
        handleFinalSubmit(updatedData);
      } else if (step === 4) {
        handleVerifyOTP(data.otp);
      } else {
        setStep((prev) => prev + 1);
      }
    }
  };

  const handleBack = () => {
    setStep((prev) => prev - 1);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire({
          icon: "warning",
          title: "File too large",
          text: "Max 2MB",
          showConfirmButton: false,
          timer: 2000,
        });
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleResendEmail = async () => {
    if (resendTimer > 0) return;

    setIsLoading(true);
    const result = await resendOTP(emailData.email);
    setIsLoading(false);

    if (result.success) {
      setResendTimer(120); // Reset to 2 minutes
      Swal.fire({
        icon: "success",
        title: "OTP Resent",
        text: `A new code was sent to ${emailData.email}`,
        confirmButtonColor: "#E63946",
        timer: 2500,
      });
    } else {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: result.message,
        confirmButtonColor: "#E63946",
      });
    }
  };

  const handleFinalSubmit = async (finalData) => {
    setIsLoading(true);
    try {
      const apiPayload = new FormData();

      const backendMapping = {
        email: finalData.email,
        password: finalData.password,
        name: finalData.fullName,
        companyName: finalData.companyName,
        mobile: finalData.phone,
        websiteUrl: finalData.website,
        businessType: finalData.businessType,
        companySize: finalData.companySize,
        country: finalData.country,
        role: finalData.adminRole,
      };

      Object.entries(backendMapping).forEach(([key, value]) =>
        apiPayload.append(key, value)
      );

      if (logoFile) apiPayload.append("profilePic", logoFile);

      const result = await registerCompany(apiPayload);
      if (result.success) {
        setIsLoading(false);
        setStep(4);
        setResendTimer(120);
        setEmailData({ email: finalData.email });
      } else {
        throw new Error(result.message || "Registration failed");
      }
    } catch (error) {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: error.message,
        confirmButtonColor: "#E63946",
      });
    }
  };

  const handleVerifyOTP = async (otp) => {
    setIsLoading(true);
    try {
      const result = await verifyOTP(emailData.email, otp);
      if (result.success) {
        setIsLoading(false);
        setAuthorization(result.data.token, result.data.companyId);
        dispatch({
          authToken: result.data.token,
          companyId: result.data.companyId,
          companydata: result.data,
        });
        setStep(5);
      } else {
        throw new Error(result.message || "Invalid OTP");
      }
    } catch (error) {
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text: error.message,
        confirmButtonColor: "#E63946",
      });
    }
  };

  const renderProgressBar = () => (
    <div className="flex items-center gap-2 mb-8">
      {[1, 2, 3, 4].map((num) => (
        <div key={num} className="flex items-center flex-1">
          <div
            className={`h-2 w-full rounded-full transition-all duration-300 ${
              step >= num ? "bg-[#E63946]" : "bg-gray-200"
            }`}
          ></div>
        </div>
      ))}
    </div>
  );

  return (
    <div>
      <div className="min-h-screen flex bg-[#F6F8FB] font-sans overflow-x-hidden pt-20 lg:pt-0">
        {step < 5 && (
          <div className="hidden lg:flex w-5/12 bg-[#0B1C2D] relative overflow-hidden items-center justify-center p-12">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-[#1a2f46] via-[#0B1C2D] to-[#0B1C2D]"></div>
            <div className="absolute top-1/4 -left-12 w-96 h-96 bg-[#E63946]/10 rounded-full blur-[100px]"></div>
            <div className="relative z-10 max-w-lg">
              <a href="/" className="flex items-center gap-3 group mb-6">
                <div className="flex items-center justify-center size-10 rounded-lg bg-[#E63946] text-white transition-transform duration-300 group-hover:scale-110 shadow-lg shadow-[#E63946]/20">
                  <span className="material-symbols-outlined">hub</span>
                </div>
                <span className="text-xl font-bold text-white">ContactHQ</span>
              </a>
              <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
                Start your journey
                <br /> with <span className="text-[#E63946]"> ContactHQ.</span>
              </h1>
              <p className="text-gray-400 text-lg leading-relaxed mb-10">
                Streamline customer queries and build stronger relationships
                from day one.
              </p>
              {/* Testimonial */}
              <div className="bg-white/5 backdrop-blur-md border border-white/10 p-6 rounded-2xl">
                <div className="flex text-yellow-400 mb-3 gap-1">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-gray-300 italic mb-4">
                  "Managing multiple client forms across Delhi was chaotic. Now,
                  we track responses and collaborate without ever touching a
                  spreadsheet. Highly recommended for agencies."
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src="https://i.pravatar.cc/100?u=Sayani Verma"
                    alt="User"
                    className="w-10 h-10 rounded-full border-2 border-[#E63946]"
                  />
                  <div>
                    <p className="text-white text-sm font-bold">Sayani Verma</p>
                    <p className="text-[#E63946] text-xs font-semibold">
                      Digital Marketing Head, Delhi
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div
          className={`flex-1 flex items-center justify-center p-4 sm:p-6 ${
            step === 5 ? "bg-[#F6F8FB] w-full" : ""
          }`}
        >
          <AnimatePresence mode="wait">
            {step === 4 ? (
              <motion.div
                key="step4"
                variants={slideUp}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="w-full max-w-lg"
              >
                <div className="bg-white p-5 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 relative">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-[#0B1C2D]">
                        Verify OTP
                      </h2>
                      <p className="text-gray-500 text-sm mt-1">
                        Check your email for the 6-digit code
                      </p>
                    </div>
                    <button
                      onClick={handleBack}
                      className="text-gray-400 hover:text-[#0B1C2D]"
                    >
                      <span className="material-symbols-outlined">
                        arrow_back
                      </span>
                    </button>
                  </div>
                  {renderProgressBar()}
                  <form
                    onSubmit={handleSubmit(handleNext)}
                    className="space-y-5"
                  >
                    <div>
                      <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                        Enter 6-Digit Code *
                      </label>
                      <input
                        {...register("otp")}
                        maxLength={6}
                        className={`w-full px-4 py-3 rounded-lg border text-center text-2xl tracking-[10px] font-bold ${
                          errors.otp ? "border-red-300" : "border-gray-200"
                        } focus:border-[#E63946] outline-none`}
                        placeholder="000000"
                      />
                      {errors.otp && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.otp.message}
                        </p>
                      )}
                    </div>
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#E63946] hover:bg-[#d12f3c] text-white font-bold py-3.5 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all"
                    >
                      {isLoading ? (
                        <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                      ) : (
                        "Verify OTP"
                      )}
                    </button>
                    <p className="text-center text-sm text-gray-500 mt-4">
                      Didn't get the code?{" "}
                      <button
                        type="button"
                        onClick={handleResendEmail}
                        disabled={resendTimer > 0}
                        className={`font-semibold ${
                          resendTimer > 0
                            ? "text-gray-400"
                            : "text-[#E63946] underline"
                        }`}
                      >
                        {resendTimer > 0
                          ? `Resend in ${resendTimer}s`
                          : "Resend OTP"}
                      </button>
                    </p>
                  </form>
                </div>
              </motion.div>
            ) : step === 5 ? (
              <motion.div
                key="step5"
                variants={slideUp}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="w-full max-w-md bg-[#0B1C2D] rounded-2xl shadow-2xl p-6 sm:p-8 text-center border border-gray-700 relative overflow-hidden"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#0B1C2D] via-[#E63946] to-[#0B1C2D]"></div>
                <div className="mx-auto w-20 h-20 bg-[#E63946]/20 rounded-2xl flex items-center justify-center mb-6">
                  <span className="material-symbols-outlined text-[#E63946] text-4xl">
                    verified
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  Verified Successfully!
                </h2>
                <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                  Your account has been activated.
                </p>
                <Link
                  to="/dashboard"
                  className="block w-full bg-[#E63946] text-white font-bold py-3 rounded-lg hover:bg-[#d12f3c] transition-colors"
                >
                  Go to Dashboard
                </Link>
              </motion.div>
            ) : (
              <div className="w-full max-w-lg">
                <div className="bg-white p-5 sm:p-8 md:p-10 rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 relative">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-[#0B1C2D]">
                        Register
                      </h2>
                      <p className="text-gray-500 text-sm mt-1">
                        Step {step} of 3
                      </p>
                    </div>
                    {step > 1 && (
                      <button
                        onClick={handleBack}
                        className="text-gray-400 hover:text-[#0B1C2D]"
                      >
                        <span className="material-symbols-outlined">
                          arrow_back
                        </span>
                      </button>
                    )}
                  </div>
                  {renderProgressBar()}
                  <form onSubmit={handleSubmit(handleNext)}>
                    <AnimatePresence mode="wait">
                      {step === 1 && (
                        <motion.div
                          key="step1"
                          variants={slideUp}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="space-y-5"
                        >
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Work Email *
                            </label>
                            <input
                              {...register("email")}
                              className={`w-full px-4 py-3 rounded-lg border ${
                                errors.email
                                  ? "border-red-300"
                                  : "border-gray-200"
                              } focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/10 outline-none`}
                              placeholder="name@company.com"
                            />
                            {errors.email && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.email.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Password *
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? "text" : "password"}
                                {...register("password")}
                                className={`w-full px-4 py-3 rounded-lg border ${
                                  errors.password
                                    ? "border-red-300"
                                    : "border-gray-200"
                                } focus:border-[#E63946] focus:ring-2 focus:ring-[#E63946]/10 outline-none`}
                                placeholder="Min. 8 characters"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-[#0B1C2D]"
                              >
                                <span className="material-symbols-outlined">
                                  {showPassword
                                    ? "visibility"
                                    : "visibility_off"}
                                </span>
                              </button>
                            </div>
                            <div className="flex gap-1 mt-2 h-1">
                              <div
                                className={`flex-1 rounded-full ${
                                  passwordValue.length > 0
                                    ? "bg-red-400"
                                    : "bg-gray-100"
                                }`}
                              ></div>
                              <div
                                className={`flex-1 rounded-full ${
                                  passwordValue.length > 7
                                    ? "bg-yellow-400"
                                    : "bg-gray-100"
                                }`}
                              ></div>
                              <div
                                className={`flex-1 rounded-full ${
                                  passwordValue.match(/[A-Z]/) &&
                                  passwordValue.match(/[!@#$%^&*]/)
                                    ? "bg-green-400"
                                    : "bg-gray-100"
                                }`}
                              ></div>
                            </div>
                            {errors.password && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.password.message}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                      {step === 2 && (
                        <motion.div
                          key="step2"
                          variants={slideUp}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="space-y-5"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-16 h-16 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50">
                              {logoPreview ? (
                                <img
                                  src={logoPreview}
                                  alt="Logo"
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="material-symbols-outlined text-gray-400">
                                  add_photo_alternate
                                </span>
                              )}
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0B1C2D] cursor-pointer hover:text-[#E63946] transition-colors">
                                Upload Logo / Profile Pic
                                <input
                                  type="file"
                                  className="hidden"
                                  accept="image/*"
                                  onChange={handleLogoUpload}
                                />
                              </label>
                              <p className="text-xs text-gray-400">Max 2MB.</p>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Company Name *
                            </label>
                            <input
                              {...register("companyName")}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                              placeholder="Acme Inc."
                            />
                            {errors.companyName && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.companyName.message}
                              </p>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                                Business Type
                              </label>
                              <select
                                {...register("businessType")}
                                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] bg-white outline-none"
                              >
                                <option value="">Select...</option>

                                <optgroup label="Technology & Digital">
                                  <option value="SaaS">Software & SaaS</option>
                                  <option value="E-commerce">E-commerce</option>
                                  <option value="Marketing">
                                    Marketing Agency
                                  </option>
                                  <option value="IT-Services">
                                    IT & Managed Services
                                  </option>
                                  <option value="EdTech">
                                    EdTech & Education
                                  </option>
                                </optgroup>

                                <optgroup label="Professional Services">
                                  <option value="Real-Estate">
                                    Real Estate
                                  </option>
                                  <option value="Legal">
                                    Legal & Law Firms
                                  </option>
                                  <option value="Financial">
                                    Finance & Insurance
                                  </option>
                                  <option value="Consulting">
                                    Business Consulting
                                  </option>
                                  <option value="Healthcare">
                                    Healthcare & Wellness
                                  </option>
                                </optgroup>

                                <optgroup label="Creative & Trade">
                                  <option value="Photography">
                                    Photography & Videography
                                  </option>
                                  <option value="Handicrafts">
                                    Handicrafts & Artisans
                                  </option>
                                  <option value="Construction">
                                    Construction & Interior Design
                                  </option>
                                  <option value="Event-Management">
                                    Event Management
                                  </option>
                                </optgroup>

                                <optgroup label="Other">
                                  <option value="NGO">Non-Profit / NGO</option>
                                  <option value="Other">Other</option>
                                </optgroup>
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                                Size
                              </label>
                              <select
                                {...register("companySize")}
                                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] bg-white outline-none"
                              >
                                <option value="">Select...</option>
                                <option value="1-10">1-10</option>
                                <option value="50+">50+</option>
                                <option value="100+">100+</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                                Country
                              </label>
                              <input
                                {...register("country")}
                                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                                placeholder="India"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                                Website URL *
                              </label>
                              <input
                                {...register("website")}
                                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                                placeholder="https://"
                              />
                            </div>
                          </div>
                        </motion.div>
                      )}
                      {step === 3 && (
                        <motion.div
                          key="step3"
                          variants={slideUp}
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          className="space-y-5"
                        >
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Full Name *
                            </label>
                            <input
                              {...register("fullName")}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                              placeholder="John Doe"
                            />
                            {errors.fullName && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.fullName.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Job Title *
                            </label>
                            <input
                              {...register("adminRole")}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                              placeholder="e.g. CEO"
                            />
                            {errors.adminRole && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.adminRole.message}
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-semibold text-[#0B1C2D] mb-1.5">
                              Phone Number *
                            </label>
                            <input
                              {...register("phone")}
                              className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-[#E63946] outline-none"
                              placeholder="1234567890"
                            />
                            {errors.phone && (
                              <p className="text-red-500 text-xs mt-1">
                                {errors.phone.message}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                    <div className="mt-8">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-[#E63946] hover:bg-[#d12f3c] text-white font-bold py-3.5 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all"
                      >
                        {isLoading ? (
                          <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span>
                        ) : (
                          <>
                            {step === 3 ? "Complete Registration" : "Next Step"}
                            <span className="material-symbols-outlined text-[18px]">
                              arrow_forward
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                    {step === 1 && (
                      <div className="mt-6 text-center">
                        <p className="text-sm text-gray-500">
                          Already have an account?{" "}
                          <Link
                            to="/login"
                            className="font-semibold text-[#E63946] hover:underline"
                          >
                            Log in
                          </Link>
                        </p>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
